import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import { fetchHeroSliders, HeroSlider } from '../api/heroSliders';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-fade';

const Hero: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { getSetting } = useSettings();
    const isRTL = i18n.dir() === 'rtl';

    const [sliders, setSliders] = useState<HeroSlider[]>([]);
    const [isLoadingSliders, setIsLoadingSliders] = useState(true);

    const heroTitle = i18n.language === 'ar' ? getSetting('hero_title_ar', t('home.heroTitle')) : getSetting('hero_title_fr', t('home.heroTitle'));
    const heroSubtitle = i18n.language === 'ar' ? getSetting('hero_subtitle_ar', t('home.heroSubtitle')) : getSetting('hero_subtitle_fr', t('home.heroSubtitle'));
    const heroBadge = i18n.language === 'ar' ? getSetting('hero_badge_ar', t('home.heroBadge')) : getSetting('hero_badge_fr', t('home.heroBadge'));
    const fallbackImage = getSetting('hero_image', 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80');

    useEffect(() => {
        const loadSliders = async () => {
            try {
                const data = await fetchHeroSliders();
                setSliders(data);
            } catch (error) {
                console.error('Failed to load hero sliders:', error);
            } finally {
                setIsLoadingSliders(false);
            }
        };
        loadSliders();
    }, []);

    return (
        <div className="relative bg-gradient-to-br from-primary-dark via-primary to-accent overflow-hidden min-h-screen flex items-center">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-20">
                <motion.div
                    className="absolute inset-0"
                    animate={{
                        backgroundPosition: ['0% 0%', '100% 100%'],
                    }}
                    transition={{
                        duration: 30,
                        repeat: Infinity,
                        repeatType: 'reverse',
                    }}
                    style={{
                        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)',
                        backgroundSize: '60px 60px',
                    }}
                />
            </div>

            {/* Floating Shapes */}
            <motion.div
                className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"
                animate={{
                    y: [0, -40, 0],
                    x: [0, 20, 0],
                    scale: [1, 1.1, 1],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: 'easeInOut',
                }}
            />
            <motion.div
                className="absolute bottom-20 right-10 w-80 h-80 bg-accent/20 rounded-full blur-3xl"
                animate={{
                    y: [0, 40, 0],
                    x: [0, -30, 0],
                    scale: [1, 1.2, 1],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: 'easeInOut',
                }}
            />

            {/* Glassmorphism Overlay */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-[2px]"></div>

            <div className="container mx-auto px-4 py-20 relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                    {/* Text Content */}
                    <div className={`w-full lg:w-1/2 ${isRTL ? 'lg:order-2 text-right' : 'lg:order-1 text-left'}`}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                        >
                            <span className="inline-block py-1 px-3 rounded-full bg-white/20 text-white text-sm font-semibold mb-6 backdrop-blur-md border border-white/30">
                                {heroBadge}
                            </span>
                        </motion.div>

                        <motion.h1
                            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight tracking-tight"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                        >
                            {heroTitle}
                        </motion.h1>

                        <motion.div
                            className="mb-12"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
                        >
                            <p className="text-xl md:text-2xl text-white/90 leading-relaxed font-light max-w-xl">
                                {heroSubtitle}
                            </p>
                        </motion.div>

                        <motion.div
                            className="flex flex-wrap gap-6"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
                        >
                            <Link
                                to="/category/all"
                                className="group relative inline-flex items-center justify-center bg-white text-primary font-bold py-4 px-10 rounded-full overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,255,255,0.5)] hover:scale-105"
                            >
                                <span className="relative z-10 flex items-center">
                                    {t('home.shopNow')}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 ${isRTL ? 'mr-2 group-hover:-translate-x-1' : 'ml-2'}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d={isRTL ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
                                        />
                                    </svg>
                                </span>
                            </Link>

                            <Link
                                to="/about"
                                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/30 text-white font-bold py-4 px-10 rounded-full hover:bg-white/20 transition-all duration-300 hover:shadow-lg hover:scale-105"
                            >
                                {t('common.learnMore')}
                            </Link>
                        </motion.div>
                    </div>

                    {/* Hero Image / Visual */}
                    <div className={`w-full lg:w-1/2 ${isRTL ? 'lg:order-1' : 'lg:order-2'} flex justify-center perspective-1000`}>
                        <motion.div
                            className="relative w-full max-w-lg"
                            initial={{ opacity: 0, rotateY: -10, scale: 0.9 }}
                            animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                            transition={{ duration: 1.2, ease: 'easeOut' }}
                        >
                            {/* Decorative elements */}
                            <div className="absolute -top-20 -right-20 w-64 h-64 bg-accent/30 rounded-full blur-3xl animate-pulse-slow"></div>
                            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-secondary/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>

                            {/* Main Image Card (Slider) */}
                            <div className="relative z-10 bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                                <div className="relative rounded-2xl overflow-hidden aspect-[4/5] shadow-inner">
                                    {sliders.length > 0 ? (
                                        <Swiper
                                            modules={[Autoplay, EffectFade]}
                                            effect="fade"
                                            speed={1000}
                                            autoplay={{
                                                delay: 5000,
                                                disableOnInteraction: false,
                                                pauseOnMouseEnter: true,
                                            }}
                                            loop={true}
                                            className="w-full h-full"
                                        >
                                            {sliders.map((slide) => (
                                                <SwiperSlide key={slide.id}>
                                                    {slide.link ? (
                                                        <a href={slide.link} target={slide.link.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer" className="block w-full h-full">
                                                            <img
                                                                src={slide.image}
                                                                alt={slide.alt || heroTitle}
                                                                className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-[2s]"
                                                            />
                                                        </a>
                                                    ) : (
                                                        <img
                                                            src={slide.image}
                                                            alt={slide.alt || heroTitle}
                                                            className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-[2s]"
                                                        />
                                                    )}
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    ) : (
                                        <img
                                            src={fallbackImage}
                                            alt={heroTitle}
                                            className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-[2s]"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none z-10"></div>

                                    {/* Floating Badge (Remains fixed on top of slider) */}
                                    <motion.div
                                        className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md px-4 py-2 rounded-xl shadow-lg z-20"
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 1, duration: 0.5 }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">{t('home.organic100')}</p>
                                                <p className="text-sm text-dark font-bold">{t('home.certifiedQuality')}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>

                            {/* Floating Element (Remains fixed outside slider) */}
                            <motion.div
                                className="absolute -right-12 top-1/4 bg-white p-4 rounded-2xl shadow-xl z-30 max-w-[180px] hidden md:block"
                                animate={{ y: [0, -15, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <p className="text-xs text-gray-500">{t('home.happyCustomers')}</p>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hero;
